import {
  radius as tamaguiRadius,
  size as tamaguiSize,
  space as tamaguiSpace,
  zIndex,
} from "@tamagui/themes";
import { createTokens } from "@tamagui/web";

/** Size tokens are based off of a 8px grid system with 100 as the base unit
 * e.g. 100 is 8px, 200 is 16px, 1000 is 80px, etc.
 */
const size = {
  0: 0,
  25: 2,
  50: 4,
  75: 6,
  100: 8,
  125: 10,
  150: 12,
  200: 16,
  225: 18,
  250: 20,
  300: 24,
  350: 28,
  375: 30,
  400: 32,
  450: 36,
  500: 40,
  550: 44,
  600: 48,
  650: 52,
  700: 56,
  800: 64,
  900: 72,
  1000: 80,
  1100: 88,
  1200: 96,
  1250: 100,
  1300: 104,
  1400: 112,
  1500: 120,
  1600: 128,
  2000: 160,
  2400: 192,
  3000: 240,
  3100: 248,
};

const negativeSize = Object.fromEntries(
  Object.entries(size).map(([k, v]) => [`-${k}`, -v]),
) as Record<`-${keyof typeof size}`, number>;

export const SIZE_CONSTANTS = {
  constraint: 728,
  landingPageConstraint: 1080,
  modalHeight: 600,
  screenBorderRadius: 12,
  screenPadding: 16,
  sidebarExpandedWidth: 250,
  sidebarCollapsedWidth: 40,
  headerHeight: 56,
  strokeWidth: 1.5,
};

// Opacity to hex helper: https://gist.github.com/alvinthen/a036671f97ea43c6b1f663cf5071b77f

export const colors = {
  neutral0: "#fff",
  neutral0_transparent_20: "#ffffff33", // 20% opacity
  neutral10: "#fefefd",
  neutral25: "#fafafa",
  neutral50: "#f5f5f5",
  neutral100: "#efefef",
  neutral200: "#e6e6e6",
  neutral300: "#ccc",
  neutral400: "#a6a6a6",
  neutral500: "#868686",
  neutral600: "#666",
  neutral700: "#3f3f3f",
  neutral700_transparent: "#3f3f3f80", // 50% opacity
  neutral800: "#282828",
  neutral800_transparent: "#28282880", // 50% opacity
  neutral850: "#1d1d1d",
  neutral900: "#121212",
  neutral900_transparent: "#12121280", // 50% opacity
  neutral1000: "#000",
  neutral1000_transparent: "#00000080", // 50% opacity

  light100: "#FEFEFD",
  light100_transparent: "#FEFEFDD9", // 85% opacity
  light200: "#F8F4F1",
  light200_transparent: "#F8F4F1B3", // 70% opacity
  light200_transparent_40: "#F8F4F166", // 40% opacity
  light250: "#F3EFEC",
  light275: "#EEEAE7",
  light300: "#E7E2DF",
  light400: "#C6C2BE",
  light400_transparent: "#C6C2BE99", // 60% opacity
  light500: "#A09A96",
  light600: "#5B544E",
  light700: "#3F3730",
  light800: "#25201C",

  dark100: "#F2F5FF",
  dark200: "#E6EBF9",
  dark300: "#D0D5E2",
  dark400: "#AFB4C1",
  dark500: "#6D727F",
  dark525: "#333A4F",
  dark550: "#2C3349",
  dark550_transparent: "#2C3349D9", // 85% opacity
  dark600: "#202538",
  dark650: "#181B2A",
  dark700: "#161A27",
  dark700_transparent: "#161A27B3", // 70% opacity
  dark800: "#0F111B",

  // Only used by the partner landing page testimonial badge
  secondary100: "#EEDDF9",
  secondary800: "#662C5D",

  curai50: "#E7EDED",
  curai100: "#D5DFDE",
  curai200: "#ACC1BD",
  curai300: "#85A49E",
  curai400: "#4F736D",
  curai500: "#0D5E52",
  curai600: "#0C4F44",
  curai700: "#093C33",
  curai800: "#062822",
  curai900: "#031411",

  tertiary50: "#FFF8EC",
  tertiary100: "#FEF3DB",
  tertiary700: "#A1710D",
  tertiary800: "#6B4B09",
  tertiary900: "#362604",

  // Partner brand colors, used only on partner landing pages
  walmartBlue: "#0053E2",
  samsClubBlue: "#0D61FF",
  workwhileMint: "#93FACE80",

  accent1: "#5967F6",
  accent2: "#EF7646",
  accent3: "#5DCBE5",

  // UI Colors
  light_red: "#a23021",
  light_yellow: "#a0581c",
  light_green: "#196038",
  light_a11yBlue: "#275DC5",
  dark_red: "#e9513d",
  dark_yellow: "#d5af4d",
  dark_green: "#2d975b",
  dark_a11yBlue: "#4180f9",

  transparent: "rgba(0,0,0,0)",
  overlay: "rgba(0,0,0,0.3)",
};

export const tokens = createTokens({
  color: colors,
  radius: {
    default: size[100],
    ...tamaguiRadius,
    ...size,
    ...SIZE_CONSTANTS,
  },
  zIndex,
  space: {
    ...tamaguiSpace,
    ...size,
    ...negativeSize,
    ...SIZE_CONSTANTS,
  },
  size: {
    ...tamaguiSize,
    ...size,
    ...SIZE_CONSTANTS,
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
});
